import type { GameCode, ProfileCard, RankInfo } from '@/types/qriots';

export type DeltaStat = 'rank' | 'wr' | 'level' | 'mastery';

export interface ComputedDeltas {
  /** Stats where side A leads. */
  A: DeltaStat[];
  /** Stats where side B leads. */
  B: DeltaStat[];
  /** Stats where both sides are equal. */
  ties: DeltaStat[];
}

// Tier order, highest first. Apex tiers (Challenger/GM/Master) all use
// division "I" — LP carries the weight for ordering inside them.
const TIER_ORDER = [
  'CHALLENGER',
  'GRANDMASTER',
  'MASTER',
  'DIAMOND',
  'EMERALD',
  'PLATINUM',
  'GOLD',
  'SILVER',
  'BRONZE',
  'IRON',
] as const;

// Division within tier. "I" = best, "IV" = worst.
const DIVISION_ORDER = ['I', 'II', 'III', 'IV'] as const;

function rankScore(r: RankInfo | undefined): number | null {
  if (!r) return null;
  const tierIdx = TIER_ORDER.indexOf(
    r.tier.toUpperCase() as (typeof TIER_ORDER)[number],
  );
  const divIdx = DIVISION_ORDER.indexOf(
    r.division.toUpperCase() as (typeof DIVISION_ORDER)[number],
  );
  if (tierIdx < 0) return null;
  // Invert indices so higher tier / division produces a higher score.
  return (
    (TIER_ORDER.length - tierIdx) * 1000 +
    (DIVISION_ORDER.length - Math.max(divIdx, 0)) * 100 +
    r.lp
  );
}

function pickRank(p: ProfileCard, game: GameCode): RankInfo | undefined {
  if (game === 'tft') return p.ranks.tft;
  if (game === 'lol') return p.ranks.soloDuo;
  return undefined; // LoR
}

/**
 * Compute per-stat winners between two ProfileCards.
 *
 * Returns three disjoint arrays: A (stats where A leads), B (stats where B
 * leads), ties (stats where both have equal values). Stats with insufficient
 * data on either side are simply omitted from all three arrays.
 */
export function computeDeltas(
  a: ProfileCard,
  b: ProfileCard,
  game: GameCode,
): ComputedDeltas {
  const out: ComputedDeltas = { A: [], B: [], ties: [] };

  // Rank + WR (LoL / TFT only).
  if (game !== 'lor') {
    const rankA = pickRank(a, game);
    const rankB = pickRank(b, game);
    const scoreA = rankScore(rankA);
    const scoreB = rankScore(rankB);
    if (scoreA !== null && scoreB !== null) {
      if (scoreA > scoreB) out.A.push('rank');
      else if (scoreB > scoreA) out.B.push('rank');
      else out.ties.push('rank');
    }
    if (rankA && rankB) {
      if (rankA.winRate > rankB.winRate) out.A.push('wr');
      else if (rankB.winRate > rankA.winRate) out.B.push('wr');
      else out.ties.push('wr');
    }
  }

  // Summoner level — always comparable when both sides have it.
  if (a.summonerLevel != null && b.summonerLevel != null) {
    if (a.summonerLevel > b.summonerLevel) out.A.push('level');
    else if (b.summonerLevel > a.summonerLevel) out.B.push('level');
    else out.ties.push('level');
  }

  // Top-mastery level (LoL / TFT only — LoR has no mastery).
  if (game !== 'lor') {
    const mA = a.topMastery?.[0];
    const mB = b.topMastery?.[0];
    if (mA && mB) {
      if (mA.level > mB.level) out.A.push('mastery');
      else if (mB.level > mA.level) out.B.push('mastery');
      else if (mA.points > mB.points) out.A.push('mastery');
      else if (mB.points > mA.points) out.B.push('mastery');
      else out.ties.push('mastery');
    }
  }

  return out;
}
